// Client-side image preparation: downscales oversized phone-camera photos before
// upload so the browser doesn't hold huge full-resolution buffers in memory across
// repeated captures. Keeps enough resolution for accurate garment analysis and
// preserves orientation. Falls back to the original file on any failure.

export const PHOTO_ERROR_MSG = "The photo couldn't be loaded. Close the camera and take another shot.";

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch { /* older browsers ignore the option */ }
    try { return await createImageBitmap(file); }
    catch { /* fall back to <img> */ }
  }
  const objUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objUrl;
    });
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

export async function prepareImage(file, { maxDim = 1600, quality = 0.85 } = {}) {
  if (!file) return file;
  // Attempt processing for images AND empty-type camera captures; skip clearly non-image files.
  if (file.type && !file.type.startsWith("image/")) return file;
  try {
    const bmp = await loadBitmap(file);
    const w = bmp.width || bmp.naturalWidth;
    const h = bmp.height || bmp.naturalHeight;
    const longest = Math.max(w, h);
    if (!longest || longest <= maxDim) { if (bmp.close) bmp.close(); return file; }

    const scale = maxDim / longest;
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bmp, 0, 0, cw, ch);
    if (bmp.close) bmp.close();

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    canvas.width = 0; canvas.height = 0; // release the canvas buffer promptly
    if (!blob) return file;

    const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
